import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Gift, ShoppingBag } from 'lucide-react';
import { authManager } from '../services/authManager';
import TrialFrame from '../components/TrialFrame';
import OracleDiscountCard from '../components/OracleDiscountCard';

const STORE_URL = 'https://tienda.numerologia-cotidiana.com/productos/numerathum-oraculo-365-agenda-numerologica-2026-digital-pdf';

const formatDate = value => value ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(value)) : '';

export default function TrialStatusPage() {
  const navigate = useNavigate();
  const [trial, setTrial] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    let cancelled = false;
    authManager.getTrialStatus().then(({ trial: data }) => {
      if (cancelled) return;
      if (!data) navigate('/pricing', { replace: true });
      else if (data.status === 'pending') navigate('/trial/start', { replace: true });
      else setTrial(data);
    }).catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [navigate]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(trial.discount.code); setCopied(true); }
    catch { setError('Puedes seleccionar y copiar tu código directamente.'); }
  };
  return <TrialFrame wide title={trial?.status === 'expired' ? 'Tu prueba terminó, tu camino sigue' : 'Tu beneficio está reservado'} subtitle="Gracias por explorar Numerathum. Consulta aquí tu acceso y tu código personal.">
    <div className="grid gap-6 lg:grid-cols-2 lg:items-start max-w-5xl mx-auto">
      {/* Columna izquierda: tu beneficio */}
      <div className="rounded-2xl bg-white p-6 sm:p-8 shadow-xl text-center">
        {error && <p role="alert" className="text-sm text-red-700 mb-4">{error}</p>}
        {!trial && !error && <p role="status">Cargando tu beneficio…</p>}
        {trial && <>
          <Gift className="w-12 h-12 mx-auto mb-4 text-morado" aria-hidden="true" />
          {trial.status === 'converted' ? <p className="text-morado font-semibold">Tu plan ya está activo. ¡Disfruta tu calendario!</p> : <>
            <p className="text-5xl font-bold text-morado">20% menos</p>
            <p className="mt-3 text-gray-600">en el primer año del plan anual <strong>Oráculo 365</strong></p>
            <p className="mt-6 text-xs uppercase tracking-widest text-gray-500">Tu código personal</p>
            <p className="mt-2 rounded-xl bg-morado/5 p-4 font-mono font-semibold text-morado break-all select-all">{trial.discount.code}</p>
            <button type="button" onClick={copy} className="mt-3 text-sm font-medium text-morado underline">{copied ? 'Código copiado' : 'Copiar código'}</button>
            <p className="mt-5 text-sm text-gray-600">{trial.discount.isEligible ? `Válido hasta el ${formatDate(trial.discount.expiresAt)}, en tu hora local.` : 'La vigencia de este beneficio ha terminado.'}</p>
            {trial.discount.isEligible && <p className="mt-4 text-sm text-gray-600">Te avisaremos cuando puedas contratar con tu beneficio. No se realizará ningún cargo automático.</p>}
            {trial.isActive && <p className="mt-4 text-sm text-morado">Tu prueba termina el {formatDate(trial.expiresAt)}, en tu hora local.</p>}
          </>}
          {['active', 'converted'].includes(trial.status) && <Link to="/app" className="mt-6 block bg-morado text-white rounded-xl py-3 font-semibold">Seguir explorando mi calendario</Link>}
          {trial.status === 'expired' && <p className="text-sm text-gray-500 mt-6">Tus datos se conservan para cuando quieras continuar.</p>}
          {trial.isActive && <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center justify-center gap-2 bg-[#d4af37] text-morado rounded-xl py-3 font-semibold hover:bg-[#d4af37]/90 transition-colors">
            <ShoppingBag className="w-5 h-5" aria-hidden="true" /> Conseguir Numerathum
          </a>}
        </>}
        <a href="mailto:soporte@numerologia-cotidiana.com" className="block mt-6 text-sm text-morado underline">Contactar a soporte</a>
        <button type="button" onClick={() => { authManager.logout(); navigate('/login'); }} className="mt-5 text-sm text-gray-500 underline">Cerrar sesión</button>
      </div>
      {/* Columna derecha: el Oráculo con el descuento del 20% */}
      {trial && <OracleDiscountCard />}
    </div>
  </TrialFrame>;
}
